import React, { useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import AdminLayout from './components/AdminLayout';
import AdminDashboard from './adminpages/AdminDashboard';
import Products from './adminpages/Products';
import Orders from './adminpages/Orders';
import Revenue from './adminpages/Revenue';
import Tests from './adminpages/Tests';
import TestQuestions from './adminpages/TestQuestions';
import Users from './adminpages/Users';
import UserHistory from './adminpages/UserHistory';
import Emails from './adminpages/Emails';
import AdminSubscriptions from './adminpages/Subscriptions';
import Coupons from './adminpages/Coupons';
import InOfficePurchase from './adminpages/InOfficePurchase';
import Shop from './components/Shop';
import Cart from './components/Cart';
import Checkout from './components/Checkout';
import TestPage from './components/TestPage';
import Header from './components/Header';
import LoginRegisterComponent from './components/LoginRegister';
import { CartProvider } from './components/CartContext';
import ProtectedRoute from './components/ProtectedRoute';
import Subscriptions from './components/ActiveSubscriptions';
import NoteCardsPage from './components/NoteCardsPage';
import OrderConfirmation from './components/OrderConfirmation';
import QuestionStats from './components/QuestionStats';
import { AuthProvider, useAuth } from './components/AuthContext';
import axios from 'axios';
import TopHeader from './components/TopHeader';
import Footer from './components/Footer';
import ResetPassword from './components/ResetPassword';
import BetaMessage from './components/BetaMessage';
import ClosureBanner from './components/ClosureBanner';
import { TestProvider } from './components/TestContext';
import Guarantee from './components/Guarantee';
import ErrorBoundary from './components/ErrorBoundary';

// Configure axios interceptor
axios.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

const AppContent = () => {
    const { showLoginModal, setShowLoginModal } = useAuth();
    const location = useLocation();
    const isAdminRoute = location.pathname.startsWith('/admin');
    const isTestPage = location.pathname.includes('/viewTests/');

    const handleLoginSuccess = (userData) => {
        setShowLoginModal(false);
    };

    return (
        <div className="App">
            <ClosureBanner />
            <BetaMessage />
            {!isAdminRoute && !isTestPage && (
                <>
                    <TopHeader />
                    <Header />
                </>
            )}

            <Routes>
                {/* Protected Admin Routes */}
                <Route
                    path="/admin/*"
                    element={
                        <ProtectedRoute>
                            <TestProvider>
                                <AdminLayout />
                            </TestProvider>
                        </ProtectedRoute>
                    }
                >
                    <Route path="dashboard" element={<AdminDashboard />} />
                    <Route path="products" element={<Products />} />
                    <Route path="test-questions" element={<TestQuestions />} />
                    <Route path="orders" element={<Orders />} />
                    <Route path="in-office-purchase" element={<InOfficePurchase />} />
                    <Route path="subscriptions" element={<AdminSubscriptions />} />
                    <Route path="revenue" element={<Revenue />} />
                    <Route path="tests" element={<Tests />} />
                    <Route path="users" element={<Users />} />
                    <Route path="user-history" element={<UserHistory />} />
                    <Route path="emails" element={<Emails />} />
                    <Route path="coupons" element={<Coupons />} />
                    <Route path="" element={<Navigate to="/admin/dashboard" />} />
                </Route>

                {/* Public Routes */}
                <Route path="shop" element={<Shop />} />
                <Route path="cart" element={<Cart />} />
                <Route path="checkout" element={<Checkout />} />
                <Route path="order-confirmation" element={<OrderConfirmation />} />
                <Route path="viewTests/:subscriptionId" element={<TestPage />} />
                <Route path="testpage" element={<TestPage />} />
                <Route path="subscriptions" element={<Subscriptions />} />
                <Route path="test-history" element={<QuestionStats />} />
                <Route path="noteCards/:subscriptionId" element={<NoteCardsPage />} />
                <Route path="/reset-password/:token" element={<ResetPassword />} />
                <Route path="guarantee" element={<Guarantee />} />
                <Route path="/" element={<Navigate to="/shop" />} />
                {/* Catch-all route */}
                <Route path="*" element={<Navigate to="/" />} />
            </Routes>

            {/* Login Modal */}
            {showLoginModal && <LoginRegisterComponent onLoginSuccess={handleLoginSuccess} onClose={() => setShowLoginModal(false)} />}

            {!isAdminRoute && <Footer />}
        </div>
    );
};

const App = () => {
    useEffect(() => {
        document.title = 'Contractor Licensing Services - Test Prep App';
    }, []);

    return (
        <ErrorBoundary>
            <AuthProvider>
                <CartProvider>
                    <Router>
                        <AppContent />
                    </Router>
                </CartProvider>
            </AuthProvider>
        </ErrorBoundary>
    );
};

export default App;
